/**
 * Edit Scenario page component
 */
import {
    Alert,
    Box,
    Button,
    Snackbar,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { scenariosApi } from '../api/scenarios';
import { ScenarioResponse, SCENARIO_UNITS } from '../types/scenario';

import { GeneralStep } from '../components/Scenarios/GeneralStep';
import { ScenarioModelStep } from '../components/Scenarios/ScenarioModelStep';
import ScenarioEconomicsStep from '../components/Scenarios/ScenarioEconomicsStep';
import { ScenarioLayersStep } from '../components/Scenarios/ScenarioLayersStep';
import { ScenarioAnaloguesStep } from '../components/Scenarios/ScenarioAnaloguesStep';
import { ScenarioEconomicResultsStep } from '../components/Scenarios/ScenarioEconomicResultsStep';
import { ScenarioCalculationsStep } from '../components/Scenarios/ScenarioCalculationsStep';
import {
    getScenarioStepLabel,
    ScenarioStep,
    ScenarioStepsSidebar,
    STEPS_SIDEBAR_COLLAPSED_WIDTH,
    STEPS_SIDEBAR_WIDTH,
} from '../components/Scenarios/ScenarioStepsSidebar';
import { useScenarioWorkflowMobile } from '@/contexts/ScenarioWorkflowMobileContext';
import { useRegisterPageHelp } from '@/hooks/useRegisterPageHelp';
import { ScenarioWellsStep } from '../components/Scenarios/ScenarioWellsStep';
import { EconomicsFormData } from '../types/scenarioEconomics';
import { parseScenarioStep, scenarioPathWithStep } from '@/utils/scenarioStep';
import type { MlMode } from '@/types/analogueMl';

interface EditScenarioPageProps {
  mainSidebarCollapsed?: boolean;
}

export const EditScenarioPage: React.FC<EditScenarioPageProps> = ({
  mainSidebarCollapsed = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState<ScenarioStep>(() =>
    parseScenarioStep(searchParams.get('step'), 'general'),
  );
  useRegisterPageHelp('Edit scenario', getScenarioStepLabel(currentStep, false));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [stepsDrawerOpen, setStepsDrawerOpen] = useState(false);
  const { register: registerScenarioMobileNav } = useScenarioWorkflowMobile();
  const openStepsDrawer = useCallback(() => setStepsDrawerOpen(true), []);
  const wellsPendingSaveRef = useRef<(() => Promise<boolean>) | null>(null);
  const [inputsDirty, setInputsDirty] = useState(false);
  const [modeSwitchBusy, setModeSwitchBusy] = useState(false);
  const loadedGeneralRef = useRef<{
    name: string;
    description: string;
    visibility_status_id: string;
    unitId: number;
    fieldId: string | null;
    fieldType: 'new' | 'existing';
    coordinate: string | null;
    ml_mode: 'default' | 'custom' | 'analogue';
  } | null>(null);

  // Visibility status UUIDs
  const PUBLIC_STATUS_UUID = '53184dbd-ff42-497e-8137-a89dda5e5f87';
  const COMPANY_PUBLIC_UUID = '20a7f07b-93c4-4188-93c1-920bbc8796c8';
  const PRIVATE_UUID = '8e3819e8-4fb5-4eef-9bcb-188e9f7059e9';

  // Scenario data state
  const [scenarioData, setScenarioData] = useState({
    fieldType: 'new' as 'new' | 'existing',
    fieldId: null as string | null,
    name: '',
    description: '',
    visibility: 'private' as 'public' | 'private' | 'company_public',
    visibility_status_id: PRIVATE_UUID,
    unitId: SCENARIO_UNITS.TONS as number,
    mode: 'ml' as 'ml' | 'manual',
    coordinate: null as string | null,
    ml_mode: 'default' as MlMode,
  });
  const [modelStepReady, setModelStepReady] = useState(true);

  // Reservoirs state
  const [layers, setLayers] = useState<any[]>([]);

  // Wells state
  const [wells, setWells] = useState<any[]>([]);

  // Economics state
  const [economicsData, setEconomicsData] = useState<EconomicsFormData>({});

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isMobile) {
      registerScenarioMobileNav(null);
      return;
    }
    registerScenarioMobileNav({
      onOpen: openStepsDrawer,
      title: getScenarioStepLabel(currentStep, false),
      readOnly: false,
    });
    return () => registerScenarioMobileNav(null);
  }, [isMobile, currentStep, registerScenarioMobileNav, openStepsDrawer]);

  // Load scenario data
  useEffect(() => {
    const loadScenarioData = async () => {
      if (!scenarioId) {
        setError('Scenario ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const scenario = await scenariosApi.getScenario(scenarioId);

        // Determine visibility status
        let visibility: 'public' | 'private' | 'company_public' = 'private';
        let visibility_status_id = PRIVATE_UUID;

        if (scenario.visibility_status_id) {
          if (scenario.visibility_status_id === PUBLIC_STATUS_UUID) {
            visibility = 'public';
            visibility_status_id = PUBLIC_STATUS_UUID;
          } else if (scenario.visibility_status_id === COMPANY_PUBLIC_UUID) {
            visibility = 'company_public';
            visibility_status_id = COMPANY_PUBLIC_UUID;
          } else {
            visibility = 'private';
            visibility_status_id = PRIVATE_UUID;
          }
        } else if (scenario.is_public) {
          // Fallback for old data
          visibility = 'public';
          visibility_status_id = PUBLIC_STATUS_UUID;
        }

        // Populate scenario data
        const nextData = {
          fieldType: (scenario.field_id ? 'existing' : 'new') as 'new' | 'existing',
          fieldId: scenario.field_id || null,
          name: scenario.name || '',
          description: scenario.description || '',
          visibility,
          visibility_status_id,
          unitId: scenario.unit_id ?? SCENARIO_UNITS.TONS,
          mode: (scenario.mode === 'manual' ? 'manual' : 'ml') as 'ml' | 'manual',
          coordinate: scenario.coordinate ?? null,
          ml_mode: (
            scenario.ml_mode === 'custom' || scenario.ml_mode === 'analogue'
              ? scenario.ml_mode
              : 'default'
          ) as MlMode,
        };
        setScenarioData(nextData);
        loadedGeneralRef.current = {
          name: nextData.name,
          description: nextData.description,
          visibility_status_id: nextData.visibility_status_id,
          unitId: nextData.unitId,
          fieldId: nextData.fieldId,
          fieldType: nextData.fieldType,
          coordinate: nextData.coordinate,
          ml_mode: nextData.ml_mode,
        };
        setInputsDirty(false);
        setModelStepReady(
          nextData.ml_mode === 'default'
          || (nextData.ml_mode === 'custom' && !!scenario.custom_model_id)
          || nextData.ml_mode === 'analogue',
        );
      } catch (err: any) {
        console.error('Failed to load scenario:', err);
        setError(err.response?.data?.detail || 'Failed to load scenario');
      } finally {
        setLoading(false);
      }
    };

    loadScenarioData();
  }, [scenarioId]);

  const handleStepChange = useCallback((step: ScenarioStep) => {
    setCurrentStep(step);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('step', step);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    const fromUrl = parseScenarioStep(searchParams.get('step'), currentStep);
    if (fromUrl !== currentStep) {
      setCurrentStep(fromUrl);
    }
    // Only sync when the query string changes (e.g. browser back/forward)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleCancel = () => {
    navigate('/app/scenarios');
  };

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleFieldTypeChange = useCallback((fieldType: 'new' | 'existing') => {
    setScenarioData(prev => ({ ...prev, fieldType, fieldId: fieldType === 'new' ? null : prev.fieldId }));
    setInputsDirty(true);
  }, []);

  const handleFieldChange = useCallback((fieldId: string | null) => {
    setScenarioData(prev => ({ ...prev, fieldId }));
    setInputsDirty(true);
  }, []);

  const handleNameChange = useCallback((name: string) => {
    setScenarioData(prev => ({ ...prev, name }));
  }, []);

  const handleDescriptionChange = useCallback((description: string) => {
    setScenarioData(prev => ({ ...prev, description }));
  }, []);

  const handleVisibilityChange = useCallback((visibility: 'public' | 'private' | 'company_public') => {
    let visibility_status_id = PRIVATE_UUID;
    if (visibility === 'public') {
      visibility_status_id = PUBLIC_STATUS_UUID;
    } else if (visibility === 'company_public') {
      visibility_status_id = COMPANY_PUBLIC_UUID;
    }
    setScenarioData(prev => ({
      ...prev,
      visibility,
      visibility_status_id
    }));
  }, []);

  const handleUnitIdChange = useCallback((unitId: number) => {
    setScenarioData(prev => ({ ...prev, unitId }));
    setInputsDirty(true);
  }, []);

  const handleCoordinateChange = useCallback((coordinate: string | null) => {
    setScenarioData(prev => ({ ...prev, coordinate }));
  }, []);

  const handleMlModeChange = useCallback((ml_mode: MlMode) => {
    setScenarioData(prev => ({ ...prev, ml_mode }));
    setInputsDirty(true);
  }, []);

  const handleModelReadyChange = useCallback((ready: boolean) => {
    setModelStepReady(ready);
  }, []);

  const handleLayersChange = useCallback((newLayers: any[]) => {
    setLayers(newLayers || []);
    setInputsDirty(true);
  }, []);

  const handleWellsChange = useCallback((newWells: any[]) => {
    setWells(newWells || []);
    setInputsDirty(true);
  }, []);

  const handleEconomicsChange = useCallback((newEconomicsData: EconomicsFormData) => {
    setEconomicsData(newEconomicsData);
    setInputsDirty(true);
  }, []);

  const registerWellsPendingSave = useCallback((handler: (() => Promise<boolean>) | null) => {
    wellsPendingSaveRef.current = handler;
  }, []);

  const handleSaveAndContinue = useCallback(async () => {
    if (!scenarioId) {
      setError('Scenario ID is required');
      return;
    }

    if (!scenarioData.name.trim()) {
      setError('Scenario name is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Ensure visibility_status_id is set
      const visibilityStatusId = scenarioData.visibility_status_id || PRIVATE_UUID;

      const updateData: any = {
        name: scenarioData.name,
        description: scenarioData.description || undefined,
        coordinate: scenarioData.coordinate || null,
        is_public: visibilityStatusId === PUBLIC_STATUS_UUID,  // Keep for backward compatibility
        visibility_status_id: visibilityStatusId,
        unit_id: scenarioData.unitId ?? SCENARIO_UNITS.TONS,
        ml_mode: scenarioData.ml_mode ?? 'default',
      };

      // Only include field_id if user selected an existing field
      if (scenarioData.fieldType === 'existing' && scenarioData.fieldId) {
        updateData.field_id = scenarioData.fieldId;
      } else if (scenarioData.fieldType === 'new') {
        updateData.field_id = null;
      }

      await scenariosApi.updateScenario(scenarioId, updateData);
      loadedGeneralRef.current = {
        name: scenarioData.name,
        description: scenarioData.description || '',
        visibility_status_id: visibilityStatusId,
        unitId: scenarioData.unitId ?? SCENARIO_UNITS.TONS,
        fieldId: scenarioData.fieldType === 'existing' ? scenarioData.fieldId : null,
        fieldType: scenarioData.fieldType,
        coordinate: scenarioData.coordinate,
        ml_mode: scenarioData.ml_mode ?? 'default',
      };
      setSuccessMessage('Scenario updated successfully!');

      // Navigate to layers step after a short delay
      setTimeout(() => {
        handleStepChange('layers');
      }, 500);
    } catch (err: any) {
      console.error('Failed to update scenario:', err);
      setError(err.response?.data?.detail || 'Failed to update scenario');
    } finally {
      setSaving(false);
    }
  }, [scenarioId, scenarioData, handleStepChange]);

  const handleSwitchToView = useCallback(async () => {
    if (!scenarioId) {
      setError('Scenario ID is required');
      return;
    }
    if (!scenarioData.name.trim()) {
      setError('Scenario name is required');
      return;
    }
    if (scenarioData.mode === 'ml' && !modelStepReady) {
      setError(
        'Configure the Model step first (accept custom model or apply analogue models) before switching to view.',
      );
      return;
    }

    const loaded = loadedGeneralRef.current;
    const visibilityStatusId = scenarioData.visibility_status_id || PRIVATE_UUID;
    const generalChanged = !loaded || (
      loaded.name !== scenarioData.name
      || loaded.description !== (scenarioData.description || '')
      || loaded.visibility_status_id !== visibilityStatusId
      || loaded.unitId !== (scenarioData.unitId ?? SCENARIO_UNITS.TONS)
      || loaded.fieldType !== scenarioData.fieldType
      || loaded.fieldId !== scenarioData.fieldId
      || loaded.coordinate !== scenarioData.coordinate
      || loaded.ml_mode !== (scenarioData.ml_mode ?? 'default')
    );
    const calcAffectingGeneral = !loaded || (
      loaded.unitId !== (scenarioData.unitId ?? SCENARIO_UNITS.TONS)
      || loaded.fieldType !== scenarioData.fieldType
      || loaded.fieldId !== scenarioData.fieldId
      || loaded.ml_mode !== (scenarioData.ml_mode ?? 'default')
    );

    try {
      setModeSwitchBusy(true);
      setError(null);

      if (generalChanged) {
        const updateData: any = {
          name: scenarioData.name,
          description: scenarioData.description || undefined,
          coordinate: scenarioData.coordinate || null,
          is_public: visibilityStatusId === PUBLIC_STATUS_UUID,
          visibility_status_id: visibilityStatusId,
          unit_id: scenarioData.unitId ?? SCENARIO_UNITS.TONS,
          ml_mode: scenarioData.ml_mode ?? 'default',
        };
        if (scenarioData.fieldType === 'existing' && scenarioData.fieldId) {
          updateData.field_id = scenarioData.fieldId;
        } else if (scenarioData.fieldType === 'new') {
          updateData.field_id = null;
        }
        await scenariosApi.updateScenario(scenarioId, updateData);
      }

      let wellsSaveFailed = false;
      if (wellsPendingSaveRef.current) {
        const ok = await wellsPendingSaveRef.current();
        if (!ok) {
          wellsSaveFailed = true;
          setError('Failed to save wells. Fix validation errors before switching to view.');
        }
      }
      if (wellsSaveFailed) {
        return;
      }

      if (inputsDirty || calcAffectingGeneral) {
        await scenariosApi.invalidateCalculations(scenarioId);
      }

      navigate(scenarioPathWithStep(`/app/scenarios/${scenarioId}`, currentStep));
    } catch (err: any) {
      console.error('Failed to switch to view mode:', err);
      setError(err.response?.data?.detail || 'Failed to save and switch to view mode');
    } finally {
      setModeSwitchBusy(false);
    }
  }, [
    scenarioId,
    scenarioData,
    modelStepReady,
    inputsDirty,
    currentStep,
    navigate,
  ]);

  const handleGoToWells = useCallback(() => {
    handleStepChange(scenarioData.mode === 'manual' ? 'analogs' : 'model');
  }, [scenarioData.mode, handleStepChange]);

  const handleGoToWellsFromModel = useCallback(() => {
    if (!modelStepReady) {
      setError('Finish model configuration before continuing to Reservoir configuration.');
      return;
    }
    handleStepChange('wells');
  }, [modelStepReady, handleStepChange]);

  const handleGoToWellsFromAnalogues = useCallback(() => {
    handleStepChange('wells');
  }, [handleStepChange]);

  const handleGoToEconomy = useCallback(() => {
    handleStepChange('economy');
  }, [handleStepChange]);

  const handleSaveAndGoToResults = useCallback(() => {
    handleStepChange('calculations');
  }, [handleStepChange]);

  const getStepContent = useCallback(() => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Typography>Loading scenario data...</Typography>
        </Box>
      );
    }

    if (error && !scenarioId) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Alert severity="error" sx={{ maxWidth: '600px' }}>
            {error}
          </Alert>
        </Box>
      );
    }

    switch (currentStep) {
      case 'general':
        return (
          <GeneralStep
            scenarioId={scenarioId}
            scenarioMode={scenarioData.mode}
            onFieldTypeChange={handleFieldTypeChange}
            onFieldChange={handleFieldChange}
            onNameChange={handleNameChange}
            onDescriptionChange={handleDescriptionChange}
            onVisibilityChange={handleVisibilityChange}
            onUnitIdChange={handleUnitIdChange}
            onCoordinateChange={handleCoordinateChange}
            initialData={scenarioData}
          />
        );
      case 'layers':
        return (
          <ScenarioLayersStep
            scenarioId={scenarioId || ''}
            onLayersChange={handleLayersChange}
            onGoToWells={handleGoToWells}
            nextLabel={scenarioData.mode === 'manual' ? 'Save and go to Analogues' : 'Save and go to Model'}
          />
        );
      case 'model':
        return (
          <ScenarioModelStep
            scenarioId={scenarioId || ''}
            coordinate={scenarioData.coordinate}
            onMlModeChange={handleMlModeChange}
            onModelReadyChange={handleModelReadyChange}
            onContinue={handleGoToWellsFromModel}
          />
        );
      case 'analogs':
        return (
          <ScenarioAnaloguesStep
            scenarioId={scenarioId || ''}
            onGoToWells={handleGoToWellsFromAnalogues}
          />
        );
      case 'wells':
        return (
          <ScenarioWellsStep
            scenarioId={scenarioId || ''}
            onWellsChange={handleWellsChange}
            onGoToEconomy={handleGoToEconomy}
            isManualMode={scenarioData.mode === 'manual'}
            onRegisterPendingSave={registerWellsPendingSave}
          />
        );
      case 'economy':
        return (
          <ScenarioEconomicsStep
            key={`economy-${scenarioId}-${currentStep}`}
            scenarioId={scenarioId || ''}
            onEconomicsChange={handleEconomicsChange}
            initialData={economicsData}
            onSaveAndGoToResults={handleSaveAndGoToResults}
          />
        );
      case 'calculations':
        return (
          <ScenarioCalculationsStep
            scenarioId={scenarioId || ''}
            onCalculationComplete={() => {}}
          />
        );
      case 'results':
        return (
          <ScenarioEconomicResultsStep scenarioId={scenarioId || ''} />
        );
      default:
        return 'Select a step to continue';
    }
  }, [currentStep, scenarioData, loading, error, scenarioId, handleFieldTypeChange, handleFieldChange, handleNameChange, handleDescriptionChange, handleVisibilityChange, handleUnitIdChange, handleCoordinateChange, handleMlModeChange, handleModelReadyChange, handleLayersChange, handleWellsChange, handleEconomicsChange, handleSaveAndGoToResults, registerWellsPendingSave, handleGoToWells, handleGoToWellsFromModel, handleGoToWellsFromAnalogues, handleGoToEconomy]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          flexDirection: 'row',
          width: '100%',
        }}
      >
        {!isMobile && (
          <Box
            component="nav"
            sx={{
              width: sidebarCollapsed ? STEPS_SIDEBAR_COLLAPSED_WIDTH : STEPS_SIDEBAR_WIDTH,
              maxWidth: sidebarCollapsed ? STEPS_SIDEBAR_COLLAPSED_WIDTH : STEPS_SIDEBAR_WIDTH,
              flexShrink: 0,
              overflow: 'hidden',
              transition: 'width 0.3s ease',
            }}
          >
            <ScenarioStepsSidebar
              currentStep={currentStep}
              onStepChange={handleStepChange}
              collapsed={sidebarCollapsed}
              onToggleCollapse={handleToggleSidebar}
              mainSidebarCollapsed={mainSidebarCollapsed}
              scenarioMode={scenarioData.mode}
              canSwitchMode={!!scenarioId}
              onSwitchMode={handleSwitchToView}
              modeSwitchBusy={modeSwitchBusy}
            />
          </Box>
        )}

        {isMobile && (
          <ScenarioStepsSidebar
            variant="temporary"
            open={stepsDrawerOpen}
            onClose={() => setStepsDrawerOpen(false)}
            currentStep={currentStep}
            onStepChange={handleStepChange}
            collapsed={false}
            scenarioMode={scenarioData.mode}
            canSwitchMode={!!scenarioId}
            onSwitchMode={handleSwitchToView}
            modeSwitchBusy={modeSwitchBusy}
          />
        )}

        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            p: { xs: 2, sm: 3 },
            overflow: 'auto',
            boxSizing: 'border-box',
          }}
        >
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Edit Scenario
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {scenarioId ? `Editing scenario (ID: ${scenarioId})` : 'Loading scenario...'}
          </Typography>
        </Box>

        {/* Step Content */}
        <Box sx={{ minHeight: 'calc(100vh - 200px)' }}>
          {getStepContent()}

          {/* Action Buttons */}
          {currentStep === 'general' && (
            <Box sx={{
              mt: 4,
              px: 2,
              display: 'flex',
              gap: 2,
            }}>
              <Button
                variant="contained"
                onClick={handleSaveAndContinue}
                disabled={!scenarioData.name.trim() || saving || loading}
                sx={{
                  minWidth: 200,
                }}
              >
                {saving ? 'Saving...' : 'Save and go to reservoirs'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleCancel}
                sx={{
                  minWidth: 100,
                }}
              >
                Cancel
              </Button>
            </Box>
          )}
        </Box>
        </Box>
      </Box>

      {/* Error and Success Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
